import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Button, Callout, Card, CardContent, CardHeader, LoadingState, StatusPill } from './ui'
import { documentsApi } from '../api/documents'
import { ermesModulesApi } from '../api/ermesModules'
import type { ErmesModuleStatus, ErmesModulesResponse } from '../api/ermesModules'

/**
 * Moduli Ermes da firmare (IVA agevolata 10%, accise agevolate, Allegato A). Il CRM compila il PDF con i dati del
 * cliente e, se salvati nella scheda cliente, firma e timbro nel riquadro (altrimenti timbro disegnato per le aziende
 * e firma a mano); l'operatore lo scarica, lo stampa, lo fa firmare e ricarica qui la copia firmata. Il pannello
 * compare solo se l'operatore e' Ermes o se ci sono moduli applicabili.
 */
function statusOf(m: ErmesModuleStatus): { tone: 'neutral' | 'warning' | 'success'; label: string } {
  if (m.signed) return { tone: 'success', label: 'Firmato' }
  if (m.generated) return { tone: 'warning', label: 'Da firmare' }
  return { tone: 'neutral', label: 'Da preparare' }
}

export function ContractErmesModules({ contractId }: { contractId: number }) {
  const [data, setData] = useState<ErmesModulesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busyKind, setBusyKind] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const uploadFor = useRef<ErmesModuleStatus | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setData(await ermesModulesApi.list(contractId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento dei moduli Ermes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(contractId)) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId])

  async function prepare(m: ErmesModuleStatus) {
    setBusyKind(m.kind)
    setError(null)
    setMessage(null)
    try {
      const doc = await ermesModulesApi.generate(contractId, m.kind)
      setMessage(`Modulo preparato: ${doc.name}`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nella preparazione del modulo')
    } finally {
      setBusyKind(null)
    }
  }

  async function onFile(file: File | undefined) {
    const m = uploadFor.current
    uploadFor.current = null
    if (fileInput.current) fileInput.current.value = ''
    if (!file || !m) return
    setBusyKind(m.kind)
    setError(null)
    setMessage(null)
    try {
      await documentsApi.upload('Contract', contractId, file, undefined, undefined, m.signedDocumentType)
      setMessage(`Copia firmata caricata: ${m.label}`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del modulo firmato')
    } finally {
      setBusyKind(null)
    }
  }

  if (loading && !data) return <LoadingState label="Caricamento moduli Ermes..." />
  if (!data) return error ? <Callout tone="error">{error}</Callout> : null
  if (!data.isErmes && data.modules.length === 0) return null

  return (
    <Card variant="outlined" className="mt-3">
      <CardHeader
        icon="draw"
        title="Moduli Ermes da firmare"
        subtitle="Il CRM compila il PDF con firma e timbro salvati nella scheda cliente; stampalo, completalo a mano se mancano e ricarica la copia firmata"
      />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {message && <Callout tone="success">{message}</Callout>}
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/*"
          hidden
          onChange={e => void onFile(e.target.files?.[0])}
        />
        {data.modules.length === 0 ? (
          <p className="text-muted md-typescale-body-small mb-0">
            Nessun modulo applicabile: servono punti di fornitura (POD o PDR) sulla pratica.
          </p>
        ) : (
          /* Elenco a blocchi, non tabella: la scheda sta in una colonna stretta e le colonne schiacciavano il testo. */
          <div className="d-flex flex-column gap-3">
            {data.modules.map(m => {
              const st = statusOf(m)
              return (
                <div key={m.kind} className="d-flex flex-column gap-2">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="md-typescale-emphasized-body-medium flex-grow-1">{m.label}</span>
                    <StatusPill tone={st.tone}>{st.label}</StatusPill>
                  </div>
                  {m.kind === 'AllegatoALuce' && (
                    <p className="md-typescale-body-small text-muted mb-0">
                      Numero progressivo = numero di contratto Ermes (dopo la bozza sul portale); settore Annuale e tariffa B.T. già spuntati. Firma e timbro nel riquadro in basso a destra; ricarica la copia firmata come foto o immagine: il caricamento la mette nei documenti aggiuntivi di Ermes.
                    </p>
                  )}
                  {m.missingFields.length > 0 && (
                    <p className="md-typescale-body-small text-warning mb-0" role="status">
                      Dati mancanti: {m.missingFields.join('; ')}
                      {data.customerId !== null && (
                        <>
                          {' — '}
                          <Link to={`/customers/${data.customerId}/edit`}>Apri la scheda cliente</Link>
                        </>
                      )}
                    </p>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      disabled={busyKind !== null}
                      onClick={() => void prepare(m)}
                    >
                      {busyKind === m.kind ? 'Attendi...' : m.generated || m.signed ? 'Prepara di nuovo' : 'Prepara modulo'}
                    </Button>
                    {m.generatedDocumentId !== null && (
                      <a
                        className="md-button md-button--outlined md-tone--primary md-button--sm md-state md-touch"
                        href={documentsApi.contentUrl(m.generatedDocumentId)}
                      >
                        Scarica
                      </a>
                    )}
                    {m.signedDocumentId !== null && (
                      <a
                        className="md-button md-button--text md-tone--primary md-button--sm md-state md-touch"
                        href={documentsApi.contentUrl(m.signedDocumentId)}
                      >
                        Scarica firmato
                      </a>
                    )}
                    <Button
                      type="button"
                      variant="outlined"
                      size="sm"
                      disabled={busyKind !== null || !(m.generated || m.signed)}
                      onClick={() => {
                        uploadFor.current = m
                        fileInput.current?.click()
                      }}
                    >
                      Carica firmato
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
